import * as React from "react";
import {
  Footer, AppHeader, EnvironmentBadge, AppLauncher, StagingBanner,
  Loader, GlyphLoader, GlyphErrorPage, ErrorBoundary,
  ThemeToggle, LanguageToggle, IconSprite, launcherApps,
} from "@weretradeit/ui-react";
import { I18nProvider } from "@weretradeit/ui-react/i18n";
import { fetchMe, type User } from "./api/client";
import { migrateLocalStorageToBackend } from "./utils/migrateLocalStorage";
/* Der Standardexport ist die i18next-Instanz DIESER App (i18n/i18n.ts ruft
   `initReactI18next` darauf auf, macht sie also zur Vorgabe-Instanz von
   react-i18next). Bis 3.12.0 stand hier ein blosses `import './i18n/i18n'` —
   die Seiteneffekte genuegten, weil kein Bedienelement die Sprache umstellte.
   Jetzt gibt es eines und es braucht die Instanz namentlich. */
import i18n from './i18n/i18n';

// Lazy load all pages for code splitting
const DashboardPage = React.lazy(() => import("./pages/DashboardPage").then(m => ({ default: m.DashboardPage })));
const OrdersPage = React.lazy(() => import("./pages/OrdersPage").then(m => ({ default: m.OrdersPage })));
const ProductsPage = React.lazy(() => import("./pages/ProductsPage").then(m => ({ default: m.ProductsPage })));
const CategoriesPage = React.lazy(() => import("./pages/CategoriesPage").then(m => ({ default: m.CategoriesPage })));
const CustomersPage = React.lazy(() => import("./pages/CustomersPage").then(m => ({ default: m.CustomersPage })));
const NewsletterPage = React.lazy(() => import("./pages/NewsletterPage").then(m => ({ default: m.NewsletterPage })));
const AnalyticsPage = React.lazy(() => import("./pages/AnalyticsPage").then(m => ({ default: m.AnalyticsPage })));
const SettingsPage = React.lazy(() => import("./pages/SettingsPage").then(m => ({ default: m.SettingsPage })));
const OrderFormsPage = React.lazy(() => import("./pages/OrderFormsPage").then(m => ({ default: m.OrderFormsPage })));
const PaymentOptionsPage = React.lazy(() => import("./pages/PaymentOptionsPage").then(m => ({ default: m.PaymentOptionsPage })));
const PaymentsPage = React.lazy(() => import("./pages/PaymentsPage").then(m => ({ default: m.PaymentsPage })));
const FeaturedProductsPage = React.lazy(() => import("./pages/FeaturedProductsPage").then(m => ({ default: m.FeaturedProductsPage })));
const HomepageHeroPage = React.lazy(() => import("./pages/HomepageHeroPage").then(m => ({ default: m.HomepageHeroPage })));
const EmailTemplatesPage = React.lazy(() => import("./pages/EmailTemplatesPage").then(m => ({ default: m.EmailTemplatesPage })));
const LegalCompliancePage = React.lazy(() => import("./pages/LegalCompliancePage").then(m => ({ default: m.LegalCompliancePage })));
const ExternalMetricsPage = React.lazy(() => import("./pages/metrics/Dashboard").then(m => ({ default: m.ExternalMetricsPage })));

type Route =
  | "/"
  | "/dashboard"
  | "/orders"
  | "/payments"
  | "/products"
  | "/categories"
  | "/customers"
  | "/newsletter"
  | "/analytics"
  | "/settings"
  | "/order-forms"
  | "/payment-options"
  | "/featured-products"
  | "/homepage-hero"
  | "/email-templates"
  | "/external-metrics"
  | "/legal";

/* „not-found“ statt stiller Umleitung (seit design-system 4.2.1).
   Bis hierher landete JEDE unbekannte Adresse — ein altes Lesezeichen wie
   `#/messages` oder `#/dlq`, ein Tippfehler — kommentarlos auf dem Dashboard:
   die Seite sah aus, als haette es geklappt. Jetzt zeigt sie die Fehlerseite der
   Flotte (`GlyphErrorPage`, 404) INNERHALB der Shell; Kopfzeile und Menue
   bleiben stehen. Leerer Hash und `#/` bleiben das Dashboard. */
type Resolved = Route | "not-found";

function normalizeHash(): Resolved {
  const hash = window.location.hash.replace(/^#/, "") || "/";
  const routes: Route[] = [
    "/",
    "/dashboard",
    "/orders",
    "/payments",
    "/products",
    "/categories",
    "/customers",
    "/newsletter",
    "/analytics",
    "/settings",
    "/order-forms",
    "/payment-options",
    "/featured-products",
    "/homepage-hero",
    "/email-templates",
    "/external-metrics",
    "/legal",
  ];
  return routes.includes(hash as Route) ? (hash as Route) : "not-found";
}

/* DIE ANWENDUNGSLISTE STEHT NICHT MEHR HIER — UND HIER WAR SIE FALSCH.
 *
 * Diese Kopie war die einzige der fuenf OHNE `PROD_URLS`: sie fuehrte
 * infrastructureDog und marketShark ueber `APP_URLS`, also ueber
 * `getPagesURL` ohne `customDomain`. Ergebnis: dieselbe Kachel im
 * SELBEN Launcher zeigte aus shopBeaver auf `infrastructure-dog.pages.dev`
 * bzw. `market-shark-pages.pages.dev`, aus allen anderen Apps auf
 * `infrastructure.weretrade.com` bzw. `shark.weretrade.com`. Auf einer
 * `.pages.dev`-Adresse greift der SSO-Cookie nicht (er ist auf
 * `.weretrade.com` begrenzt) — der Sprung landete hinter einem 401.
 *
 * `launcherApps('shopBeaver')` ist die eine Kopie und kennt die
 * Produktions-Hostnamen selbst. `AppLauncherApp` als Typimport ist damit
 * ebenfalls weg. Seit dem 02.10.2026 kommt sie aus @weretradeit/ui-react
 * (Registry des Design-Systems, `v3/fleet-apps.json`) statt aus env-helpers:
 * dort standen sechs Apps, in der Registry alle zwoelf Launcher-Apps —
 * mailBird und fuenf weitere fehlten im Raster. Dieselbe Liste zeichnet
 * `FleetChrome`. */

/* LADEN WIE IN JEDER APP DER FLOTTE (seit dem 02.10.2026).
   Hier standen `GlobalOverlay` (`.overlay` mit eigenem Kasten, Schleier
   `rgba(0,0,0,.5)` auf z-index 9999) und `PageLoader` (`min-height: 50vh`).
   Jede App der Flotte hatte ihre eigene Fassung, und die Wartebilder sahen in
   keinen zwei Apps gleich aus oder standen am selben Ort. Der Vertrag des
   Design-Systems (FLEET-CONTRACT.md § 6, Regeln L3/L6): Vollbild-Warten ist
   `<Loader overlay="page" label>` — Schleier aus `--bg`, Ebene
   `--z-modal-backdrop` —, Warten im Inhalt `<Loader label>`. „Session wird
   geprüft" ist der Text, auf den die E2E-Fixtures (shop-auth) warten.
   Seit design-system 4.2.1 ist das Vollbild-Warten `GlyphLoader` (dieselbe
   Ebene, dazu die Szene `shopBeaver:login`); im Inhalt bleibt `<Loader>`.
   `.overlay` in styles.css bleibt: der Anmeldefehler unten und OrdersPage
   benutzen es noch. */

// Navigation icons as inline SVG
const Icons = {
  Dashboard: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
  ),
  Orders: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2" />
      <rect x="9" y="3" width="6" height="4" rx="1" />
      <path d="M9 12h6M9 16h6" />
    </svg>
  ),
  Products: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" />
      <path d="M3.27 6.96L12 12.01l8.73-5.05M12 22.08V12" />
    </svg>
  ),
  Categories: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z" />
    </svg>
  ),
  Customers: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
    </svg>
  ),
  Newsletter: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
      <path d="M22 6l-10 7L2 6" />
    </svg>
  ),
  Analytics: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M18 20V10M12 20V4M6 20v-6" />
    </svg>
  ),
  Settings: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z" />
    </svg>
  ),
  OrderForms: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M7 7h10M7 12h10M7 17h6" />
    </svg>
  ),
  PaymentOptions: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="1" y="4" width="22" height="16" rx="2" />
      <path d="M1 10h22" />
    </svg>
  ),
  Payments: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
    </svg>
  ),
  FeaturedProducts: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
    </svg>
  ),
  HomepageHero: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
      <path d="M9 22V12h6v10" />
    </svg>
  ),
  EmailTemplates: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
      <polyline points="22,6 12,13 2,6"></polyline>
    </svg>
  ),
  Legal: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3v18" />
      <path d="M5 7h14" />
      <path d="M5 7l-3 7a3 3 0 006 0z" />
      <path d="M19 7l-3 7a3 3 0 006 0z" />
      <path d="M8 21h8" />
    </svg>
  ),
};

interface NavItemProps {
  route: Route;
  label: string;
  icon: React.FC;
  currentRoute: Resolved;
  onClick: (route: Route) => void;
}

const NavItem: React.FC<NavItemProps> = ({ route, label, icon: Icon, currentRoute, onClick }) => (
  <button
    className={`nav-item ${currentRoute === route ? "active" : ""}`}
    onClick={() => onClick(route)}
    type="button"
  >
    <Icon />
    <span>{label}</span>
  </button>
);

export const App: React.FC = () => {
  const [route, setRoute] = React.useState<Resolved>(normalizeHash());
  const [user, setUser] = React.useState<User | null>(null);
  const [authLoading, setAuthLoading] = React.useState(true);
  const [authError, setAuthError] = React.useState<string | null>(null);

  React.useEffect(() => {
    const handler = () => setRoute(normalizeHash());
    window.addEventListener("hashchange", handler);
    return () => window.removeEventListener("hashchange", handler);
  }, []);

  React.useEffect(() => {
    void (async () => {
      try {
        const me = await fetchMe();
        setUser(me);
      } catch (err) {
        // fetchMe only throws when the auth endpoint itself is broken (a 401
        // redirects instead). Show it rather than bouncing to login again —
        // that bounce is what turned a routing mistake into an endless loop.
        const message = err instanceof Error ? err.message : String(err);
        console.error("[Auth] Session check failed:", err);
        setAuthError(message);
      } finally {
        setAuthLoading(false);
      }
    })();
  }, []);

  // Auto-migrate localStorage data to backend on first admin visit
  React.useEffect(() => {
    if (!user || authLoading) return;

    void (async () => {
      const migrated = await migrateLocalStorageToBackend();
      if (migrated) {
        console.log('✅ Shop settings migrated from localStorage to backend');
      }
    })();
  }, [user, authLoading]);

  function navigate(next: Route) {
    window.location.hash = next === "/" ? "" : `#${next}`;
    setRoute(next);
  }

  /* Seit design-system 4.2.1 mit der eigenen Szene: `GlyphLoader` statt des
     Kreisels `<Loader overlay="page">` — GLYPHFIELD.md § 3, „ein erster
     Ladebildschirm, der sonst nichts zu zeigen hat“. Vorgabe overlay="page";
     der Text steht als role="status" daneben, das Feld ist Kunst (Szene aus
     main.tsx). Beide Saetze bleiben wortgleich: auf „Session wird geprüft“
     warten die E2E-Fixtures (shop-auth). Die Weiterleitung benutzt dieselbe
     Flaeche, damit zwischen Pruefung und Login kein Kreisel aufblitzt. */
  if (authLoading) {
    return <GlyphLoader appKey="shopBeaver" label="Session wird geprüft…" />;
  }

  if (authError) {
    return (
      <>
        <div className="app-layout" />
        <div className="overlay">
          <div className="overlay-box">
            <div className="label">Anmeldung nicht möglich</div>
            <p>{authError}</p>
            <button type="button" onClick={() => window.location.reload()}>
              Erneut versuchen
            </button>
          </div>
        </div>
      </>
    );
  }

  if (!user) {
    return <GlyphLoader appKey="shopBeaver" label="Weiterleitung zu Login…" />;
  }

  const effectiveRoute = route === "/" ? "/dashboard" : route;

  return (
    <I18nProvider>
      <StagingBanner />
      <IconSprite />
      <div className="app-layout">
      <AppHeader
        appName="Shop Admin"
        orgName="weretradeIT"
        mark="shopBeaver"
        actions={
          <>
            {/* Der Launcher stand bis 3.12.0 HIER DRUEBER, als Geschwister von
                <AppHeader> und damit als erstes Kind von `.app-layout` — eine
                eigene Reihe oberhalb der Leiste. `app-shell.css` ordnet ihn
                als `.app-header__actions > .app-launcher { order: 1 }`;
                ausserhalb dieses Behaelters trifft die Regel nichts. */}
            <AppLauncher apps={launcherApps('shopBeaver')} current="shopBeaver" />
            {/* Der Sprachumschalter fehlte, obwohl shopBeaver die App mit den
                MEISTEN Uebersetzungen ist (i18n/translations/de.json und
                en.json, react-i18next in den Seiten). Der Grund war die
                Signatur: bis 3.11.x war `LanguageToggle` gesteuert und
                brauchte einen Zustand daneben. Seit 3.12.0 haelt er `wt-lang`
                selbst; `onChange` bleibt, weil es die i18next-Instanz DIESER
                App nachzieht — die Anzeige haengt am gemeinsamen Schluessel,
                die Saetze an der Instanz. */}
            <LanguageToggle onChange={(l) => { void i18n.changeLanguage(l); }} />
          </>
        }
        /* Seit ui-react 3.15.0 setzt die Kopfzeile HINTER `actions` eigene Montagepunkte,
           sobald der passende Slot fehlt (`#theme-toggle-mount`, `#wt-user-widget-mount`).
           Mit 4.0.0 stand dadurch eine ZWEITE `#wt-user-widget-mount` in der Aktionsleiste —
           doppelte id; authLions Flotten-E2E (auth-account-access) brach daran ab.
           Seit dem 02.10.2026 die Aufteilung von `FleetChrome` (ui-react 4.0.0), wie in
           jeder App der Flotte: Thema in `themeSlot`, Umgebung + Benutzer in `userSlot`.
           `brandingSlot={false}` unterdrueckt den leeren Branding-Anker — NICHT `null`:
           4.0.0 prueft mit `??` und liest null als „kein Slot“ (behoben ab 4.0.1,
           weretrade-ecosystem PR 43). */
        themeSlot={<ThemeToggle />}
        /* 0.7.x rendete die Halterung `#wt-user-widget-mount` selbst, sobald
           showUserWidget gesetzt war; 3.x kennt die Eigenschaft nicht mehr. Ohne
           sie faende /assets/user-widget.js seinen Ankerpunkt nicht und das
           Widget verschwaende, ohne dass etwas bricht. Sie bleibt LETZTES Kind
           (app-shell.css gibt `#wt-user-widget-mount` order 5). Der Wechsel auf
           `<UserWidget>` aus ui-react folgt mit 4.0.1 — 4.0.0 ordnet
           `.user-widget` in der Aktionsleiste nicht ein. */
        userSlot={
          <>
            <EnvironmentBadge hideInProduction />
            <div id="wt-user-widget-mount" />
          </>
        }
        brandingSlot={false}
      />
      <div className="app-shell">
        <aside className="sidebar">
          <nav className="sidebar-nav">
            <div className="nav-section">Übersicht</div>
            <NavItem route="/dashboard" label="Dashboard" icon={Icons.Dashboard} currentRoute={effectiveRoute} onClick={navigate} />
            <NavItem route="/analytics" label="Analytics" icon={Icons.Analytics} currentRoute={effectiveRoute} onClick={navigate} />
            <NavItem route="/external-metrics" label="External Metrics" icon={Icons.Analytics} currentRoute={effectiveRoute} onClick={navigate} />

            <div className="nav-section">Shop</div>
            <NavItem route="/orders" label="Bestellungen" icon={Icons.Orders} currentRoute={effectiveRoute} onClick={navigate} />
            <NavItem route="/payments" label="Zahlungen" icon={Icons.Payments} currentRoute={effectiveRoute} onClick={navigate} />
            <NavItem route="/products" label="Produkte" icon={Icons.Products} currentRoute={effectiveRoute} onClick={navigate} />
            <NavItem route="/categories" label="Kategorien" icon={Icons.Categories} currentRoute={effectiveRoute} onClick={navigate} />

            <div className="nav-section">Konfiguration</div>
            <NavItem route="/order-forms" label="Bestellformulare" icon={Icons.OrderForms} currentRoute={effectiveRoute} onClick={navigate} />
            <NavItem route="/payment-options" label="Zahlungsarten" icon={Icons.PaymentOptions} currentRoute={effectiveRoute} onClick={navigate} />
            <NavItem route="/featured-products" label="Featured Products" icon={Icons.FeaturedProducts} currentRoute={effectiveRoute} onClick={navigate} />
            <NavItem route="/homepage-hero" label="Homepage Hero" icon={Icons.HomepageHero} currentRoute={effectiveRoute} onClick={navigate} />
            <NavItem route="/email-templates" label="E-Mail Templates" icon={Icons.EmailTemplates} currentRoute={effectiveRoute} onClick={navigate} />

            <div className="nav-section">Kunden</div>
            <NavItem route="/customers" label="Kunden" icon={Icons.Customers} currentRoute={effectiveRoute} onClick={navigate} />
            <NavItem route="/newsletter" label="Newsletter" icon={Icons.Newsletter} currentRoute={effectiveRoute} onClick={navigate} />

            <div className="nav-section">System</div>
            <NavItem route="/settings" label="Einstellungen" icon={Icons.Settings} currentRoute={effectiveRoute} onClick={navigate} />
            <NavItem route="/legal" label="Rechtliches" icon={Icons.Legal} currentRoute={effectiveRoute} onClick={navigate} />
          </nav>
        </aside>

        <main className="main-content">
          {/* Die Wurzelgrenze fuer jeden Reiter (seit design-system 4.2.1):
              `appKey` und KEIN eigenes `fallback` — ein `fallback` gewaenne in
              ErrorBoundary vor `appKey`. Ein Absturz zeichnet damit
              `GlyphErrorPage kind="500"` mit der Szene dieser App, der Kennung
              zum Melden und „Erneut versuchen“ (= reset, ohne Neuladen). Bis
              hierher gab es KEINE Grenze: ein Fehler in einer Seite riss die
              ganze Oberflaeche samt Menue weg (leere Seite). Innerhalb von
              `.main-content`, damit Kopfzeile und Menue stehen bleiben;
              `key` setzt die Grenze beim Reiterwechsel zurueck, sonst bliebe
              die 500 auch auf dem naechsten Reiter stehen. */}
          <ErrorBoundary key={effectiveRoute} appKey="shopBeaver">
          <React.Suspense fallback={<Loader size="lg" label="Seite wird geladen" />}>
            {effectiveRoute === "not-found" && <GlyphErrorPage appKey="shopBeaver" />}
            {effectiveRoute === "/dashboard" && <DashboardPage />}
            {effectiveRoute === "/orders" && <OrdersPage />}
            {effectiveRoute === "/payments" && <PaymentsPage />}
            {effectiveRoute === "/products" && <ProductsPage />}
            {effectiveRoute === "/categories" && <CategoriesPage />}
            {effectiveRoute === "/customers" && <CustomersPage />}
            {effectiveRoute === "/newsletter" && <NewsletterPage />}
            {effectiveRoute === "/analytics" && <AnalyticsPage />}
            {effectiveRoute === "/settings" && <SettingsPage />}
            {effectiveRoute === "/order-forms" && <OrderFormsPage />}
            {effectiveRoute === "/payment-options" && <PaymentOptionsPage />}
            {effectiveRoute === "/featured-products" && <FeaturedProductsPage />}
            {effectiveRoute === "/homepage-hero" && <HomepageHeroPage />}
            {effectiveRoute === "/email-templates" && <EmailTemplatesPage />}
            {effectiveRoute === "/external-metrics" && <ExternalMetricsPage />}
            {effectiveRoute === "/legal" && <LegalCompliancePage />}
          </React.Suspense>
          </ErrorBoundary>
        </main>
        <Footer />
      </div>
      </div>
    </I18nProvider>
  );
};
